import { useEffect } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useTopLoader } from '@/components/ui/top-loader';

interface UseQueryLoaderOptions {
  // Delay before showing the loader (in ms)
  delay?: number;
  // Whether to show loader for fetching operations
  showOnFetch?: boolean;
  // Whether to show loader for mutation operations
  showOnMutate?: boolean;
}

export const useQueryLoader = (options: UseQueryLoaderOptions = {}) => {
  const {
    delay = 100,
    showOnFetch = true,
    showOnMutate = true,
  } = options;

  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  const { start, finish } = useTopLoader();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const shouldShowLoader = 
      (showOnFetch && isFetching > 0) || 
      (showOnMutate && isMutating > 0);

    if (shouldShowLoader) {
      // Add a small delay to avoid flickering for fast requests
      timeoutId = setTimeout(() => {
        start();
      }, delay);
    } else {
      finish();
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isFetching, isMutating, start, finish, delay, showOnFetch, showOnMutate]);
};